import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NamiCampaignManager,
  NamiPaywallLaunchContext,
} from '@namiml/web-sdk';
import EditableButton from './EditableButton';
import ErrorMessage from './ErrorMessage';
import { games } from '../data/games';
import { NAMI_PAYWALL_CONTEXT } from '../utils/const';

const MATCHUP_IMAGE_URL =
  'https://imagecomposer.nfl.com/image/fetch/q_80,f_auto,h_2160,w_3840,c_fit/l_fetch:aHR0cHM6Ly9zdGF0aWMud3d3Lm5mbC5jb20vbGVhZ3VlL2FwcHMvc2hhcmVkL05FX0F3YXlfVmVydGljYWwucG5n,h_2160,w_3840,c_fit,x_0/https:/static.www.nfl.com/league/apps/shared/CLE_Home_Vertical.png';

type LaunchContextProps = {
  label?: string | null;
  url?: string | null;
  launchHandler: (
    context: NamiPaywallLaunchContext,
    appSuppliedVideoDelay: number
  ) => void;
};

const LaunchContext = ({ label, url, launchHandler }: LaunchContextProps) => {
  const [products, setProducts] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productGroupLoaded, setProductGroupLoaded] = useState(false);
  const [productPage, setProductPage] = useState(0);
  const PRODUCT_PAGE_SIZE = 6;
  const [gamesData, setGamesData] = useState(false);
  const [matchUpImage, setMatchUpImage] = useState(false);
  const [customAttributes, setCustomAttributes] = useState(
    '{"key1":"value1","key2":"value2"}'
  );
  const [videoDelay, setVideoDelay] = useState('0');
  const totalProductPages = useMemo(() => {
    if (!products.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(products.length / PRODUCT_PAGE_SIZE));
  }, [products.length]);

  const visibleProducts = useMemo(() => {
    if (!products.length) {
      return [];
    }
    const start = productPage * PRODUCT_PAGE_SIZE;
    return products.slice(start, start + PRODUCT_PAGE_SIZE);
  }, [productPage, products, PRODUCT_PAGE_SIZE]);

  const [error, setError] = useState('');

  const productGroupLabel = useMemo(() => {
    if (!label && !url) return 'Product groups';
    return `Product groups for ${label ?? url}`;
  }, [label, url]);

  const toggleProductGroups = useCallback(() => {
    if (!productGroupLoaded) {
      const groups = NamiCampaignManager.getProductGroups(label ?? undefined, url ?? undefined) ?? [];
      setProducts([...groups, 'bad_group_1', 'bad_group_2']);
      setProductGroupLoaded(true);
      setProductPage(0);
    }
  }, [label, productGroupLoaded, url]);

  const toggleProductSelection = useCallback((product: string) => {
    setSelectedProducts((prev) =>
      prev.includes(product)
        ? prev.filter((item) => item !== product)
        : [...prev, product]
    );
  }, []);

  useEffect(() => {
    if (!products.length) {
      setProductPage(0);
      return;
    }
    if (productPage > totalProductPages - 1) {
      setProductPage(totalProductPages - 1);
    }
  }, [products.length, productPage, totalProductPages]);

  const handleLaunch = useCallback(async () => {
    try {
      const parsedAttributes = JSON.parse(customAttributes);
      const context: NamiPaywallLaunchContext = {
        customAttributes: { ...parsedAttributes },
      };
      if (matchUpImage) {
        context.customAttributes.matchUpImage = MATCHUP_IMAGE_URL;
      }
      if (selectedProducts.length > 0) {
        context.productGroups = selectedProducts;
      }
      if (gamesData) {
        context.customObject = games;
      }
      localStorage.setItem(NAMI_PAYWALL_CONTEXT, JSON.stringify(context));
      launchHandler(context, Number(videoDelay) || 0);
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to prepare launch context: ${details}`);
    }
  }, [
    customAttributes,
    launchHandler,
    matchUpImage,
    selectedProducts,
    videoDelay,
    gamesData
  ]);

  return (
    <div className="panel stack">
      <h2 className="panel-title">Paywall Launch Context</h2>
      <p className="panel-subtitle">
        Build a context payload, then launch by pressing OK.
      </p>
      {error && <ErrorMessage message={error} />}

      <button
        type="button"
        className={`tv-button${gamesData ? ' active' : ''}`}
        onClick={() => setGamesData((prev) => !prev)}
      >
        {gamesData ? 'Use Games Data: Enabled' : 'Use Games Data: Disabled'}
      </button>

      <button
        type="button"
        className={`tv-button${matchUpImage ? ' active' : ''}`}
        onClick={() => setMatchUpImage((prev) => !prev)}
      >
        {matchUpImage ? 'Matchup Image: Enabled' : 'Matchup Image: Disabled'}
      </button>

      <EditableButton
        label="Custom Attributes (JSON)"
        value={customAttributes}
        onSubmit={setCustomAttributes}
      />

      <div className="panel">
        <div className="panel-title">{productGroupLabel}</div>
        <div className="panel-subtitle">Toggle to add product groups to the launch context.</div>
        <div className="option-grid" data-layout="column">
          <button
            type="button"
            className="tv-button"
            onClick={toggleProductGroups}
          >
            {productGroupLoaded ? 'Refresh Product Groups' : 'Load Product Groups'}
          </button>
          {productGroupLoaded && products.length > 0 && (
            <>
              <div className="option-grid" data-layout="column">
                {visibleProducts.map((product) => (
                  <button
                    key={product}
                    type="button"
                    className={`tv-button${selectedProducts.includes(product) ? ' active' : ''}`}
                    onClick={() => toggleProductSelection(product)}
                  >
                    {product}
                  </button>
                ))}
              </div>
              {totalProductPages > 1 && (
                <div className="inline-actions" style={{ justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    className="tv-button"
                    onClick={() => setProductPage((prev) => (prev - 1 + totalProductPages) % totalProductPages)}
                  >
                    Previous
                  </button>
                  <span className="helper-text">Page {productPage + 1} of {totalProductPages}</span>
                  <button
                    type="button"
                    className="tv-button"
                    onClick={() => setProductPage((prev) => (prev + 1) % totalProductPages)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EditableButton
        label="App Supplied Video Delay (seconds)"
        value={videoDelay}
        onSubmit={setVideoDelay}
      />

      <button type="button" className="tv-button" onClick={handleLaunch}>
        Launch Paywall
      </button>
    </div>
  );
};

export default LaunchContext;
